import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePresetContext } from "@/components/PresetProvider";
import { AlertConditionType, AlertRule, AlertSeverity, AlertSourceType } from "@/models/AlertRule";
import { WarningType } from "@/models/Warning";

interface AlertRuleFormProps {
  rule?: AlertRule;
  onSave: (rule: AlertRule) => void;
  onCancel: () => void;
}

type ValueKind = "numeric" | "discrete" | "presence";

interface SourceOption {
  id: string;
  name: string;
  sourceType: AlertSourceType;
  group: string;
  valueKind: ValueKind;
  discreteValues?: { value: number; label: string }[];
}

const NUMERIC_CONDITIONS: { value: AlertConditionType; labelKey: string }[] = [
  { value: "above", labelKey: "alertRules.conditionAbove" },
  { value: "below", labelKey: "alertRules.conditionBelow" },
  { value: "between", labelKey: "alertRules.conditionBetween" },
  { value: "outside", labelKey: "alertRules.conditionOutside" },
  { value: "equals", labelKey: "alertRules.conditionEquals" },
  { value: "changes", labelKey: "alertRules.conditionChanges" },
];

const DISCRETE_CONDITIONS: { value: AlertConditionType; labelKey: string }[] = [
  { value: "equals", labelKey: "alertRules.conditionEquals" },
  { value: "changes", labelKey: "alertRules.conditionChanges" },
];

const needsSingleThreshold = (condition: AlertConditionType) =>
  condition === "above" || condition === "below" || condition === "equals";

const needsRangeThreshold = (condition: AlertConditionType) =>
  condition === "between" || condition === "outside";

const AlertRuleForm: React.FC<AlertRuleFormProps> = ({ rule, onSave, onCancel }) => {
  const { t } = useTranslation();
  const { selectedPreset } = usePresetContext();

  const columns = selectedPreset?.columns ?? [];

  const sourceOptions = useMemo<SourceOption[]>(() => {
    const diagnosticGroup = t("alertRules.diagnostic");
    const fieldGroup = t("alertRules.field");
    const warningGroup = t("alertRules.warningType");

    const diagnostics: SourceOption[] = columns.map(col => ({
      id: col.diagnostic.id,
      name: col.diagnostic.name ?? col.diagnostic.id,
      sourceType: "diagnostic" as const,
      group: diagnosticGroup,
      valueKind: "numeric" as const,
    }));

    const fields: SourceOption[] = [
      {
        id: "ignitionStatus", name: t("alertRules.fieldIgnitionStatus"),
        sourceType: "field", group: fieldGroup, valueKind: "discrete",
        discreteValues: [
          { value: 0, label: "Aus" },
          { value: 1, label: "An" },
        ],
      },
      {
        id: "isCharging", name: t("alertRules.fieldIsCharging"),
        sourceType: "field", group: fieldGroup, valueKind: "discrete",
        discreteValues: [
          { value: -1, label: "N/A" },
          { value: 0, label: "Nein" },
          { value: 1, label: "Ja" },
          { value: 3, label: "Unterbrochen" },
        ],
      },
      {
        id: "faultPriority", name: t("alertRules.fieldFaultPriority"),
        sourceType: "field", group: fieldGroup, valueKind: "discrete",
        discreteValues: [
          { value: 0, label: "N/A" },
          { value: 1, label: "Info" },
          { value: 2, label: "Niedrig" },
          { value: 3, label: "Mittel" },
          { value: 4, label: "Hoch" },
        ],
      },
      { id: "chargeRate", name: t("alertRules.fieldChargeRate"), sourceType: "field", group: fieldGroup, valueKind: "numeric" },
      { id: "odometer", name: t("alertRules.fieldOdometer"), sourceType: "field", group: fieldGroup, valueKind: "numeric" },
    ];

    const warnings: SourceOption[] = [
      { id: WarningType.TT_RED, name: t("alertRules.warningRedTellTale"), sourceType: "warning", group: warningGroup, valueKind: "presence" },
      { id: WarningType.TT_YELLOW, name: t("alertRules.warningYellowTellTale"), sourceType: "warning", group: warningGroup, valueKind: "presence" },
      { id: WarningType.PM, name: t("alertRules.warningPredictiveMaintenance"), sourceType: "warning", group: warningGroup, valueKind: "presence" },
      { id: WarningType.PM1, name: t("alertRules.warningPredictiveMaintenance1"), sourceType: "warning", group: warningGroup, valueKind: "presence" },
      { id: WarningType.AD, name: t("alertRules.warningAccident"), sourceType: "warning", group: warningGroup, valueKind: "presence" },
      { id: WarningType.SB, name: t("alertRules.warningSeatbelt"), sourceType: "warning", group: warningGroup, valueKind: "presence" },
    ];

    return [...diagnostics, ...fields, ...warnings];
  }, [columns, t]);

  const findInitialSource = (): SourceOption | null => {
    if (!rule) return null;
    const id = rule.diagnosticId ?? rule.fieldId ?? rule.warningType ?? "";
    return sourceOptions.find(o => o.id === id && o.sourceType === rule.sourceType) ?? null;
  };

  const [name, setName] = useState(rule?.name ?? "");
  const [selectedSource, setSelectedSource] = useState<SourceOption | null>(findInitialSource);
  const [conditionType, setConditionType] = useState<AlertConditionType>(rule?.conditionType ?? "above");
  const [threshold, setThreshold] = useState<string>(rule?.threshold?.toString() ?? "");
  const [thresholdMin, setThresholdMin] = useState<string>(rule?.thresholdMin?.toString() ?? "");
  const [thresholdMax, setThresholdMax] = useState<string>(rule?.thresholdMax?.toString() ?? "");
  const [severity, setSeverity] = useState<AlertSeverity>(rule?.severity ?? "warning");
  const [targetAll, setTargetAll] = useState(rule?.targetAll ?? true);
  const [deviceIdsText, setDeviceIdsText] = useState(rule?.targetDeviceIds?.join(", ") ?? "");
  const [soundEnabled, setSoundEnabled] = useState(rule?.soundEnabled ?? false);
  const [soundRepeating, setSoundRepeating] = useState(rule?.soundRepeating ?? false);

  const valueKind = selectedSource?.valueKind ?? "numeric";
  const conditionOptions = valueKind === "discrete" ? DISCRETE_CONDITIONS : NUMERIC_CONDITIONS;

  // Reset condition when source changes to an incompatible kind
  const handleSourceChange = (source: SourceOption | null) => {
    setSelectedSource(source);
    if (source?.valueKind === "presence") {
      setConditionType("equals");
      setThreshold("1");
    } else if (source?.valueKind === "discrete") {
      if (conditionType !== "equals" && conditionType !== "changes") {
        setConditionType("equals");
      }
      setThreshold("");
    } else if (source?.valueKind === "numeric") {
      setThreshold("");
    }
  };

  const handleSave = () => {
    if (!selectedSource) return;

    // For warnings, force equals 1 (has warning)
    const effectiveCondition = valueKind === "presence" ? "equals" as const : conditionType;
    const effectiveThreshold = valueKind === "presence" ? 1 : (needsSingleThreshold(effectiveCondition) ? Number(threshold) : undefined);

    const alertRule: AlertRule = {
      id: rule?.id ?? crypto.randomUUID(),
      name,
      enabled: rule?.enabled ?? true,
      sourceType: selectedSource.sourceType,
      diagnosticId: selectedSource.sourceType === "diagnostic" ? selectedSource.id : undefined,
      fieldId: selectedSource.sourceType === "field" ? selectedSource.id : undefined,
      warningType: selectedSource.sourceType === "warning" ? selectedSource.id : undefined,
      displayName: selectedSource.name,
      conditionType: effectiveCondition,
      threshold: effectiveThreshold,
      thresholdMin: needsRangeThreshold(effectiveCondition) ? Number(thresholdMin) : undefined,
      thresholdMax: needsRangeThreshold(effectiveCondition) ? Number(thresholdMax) : undefined,
      severity,
      targetAll,
      targetDeviceIds: targetAll
        ? undefined
        : deviceIdsText
            .split(",")
            .map(s => s.trim())
            .filter(Boolean),
      soundEnabled,
      soundRepeating: soundEnabled ? soundRepeating : false,
    };
    onSave(alertRule);
  };

  const isValid = (): boolean => {
    if (!name.trim()) return false;
    if (!selectedSource) return false;
    if (valueKind === "presence") return true; // No threshold needed
    if (valueKind === "discrete" && conditionType === "equals" && selectedSource?.discreteValues &&
        !selectedSource.discreteValues.some(dv => dv.value.toString() === threshold)) return false;
    if (needsSingleThreshold(conditionType) && threshold === "") return false;
    if (needsRangeThreshold(conditionType) && (thresholdMin === "" || thresholdMax === "")) return false;
    if (!targetAll && !deviceIdsText.trim()) return false;
    return true;
  };

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {/* Name */}
      <TextField
        label={t("alertRules.name")}
        value={name}
        onChange={e => setName(e.target.value)}
        required
        size="small"
        fullWidth
      />

      {/* Source (diagnostic / field / warning) */}
      <Autocomplete
        size="small"
        options={sourceOptions}
        groupBy={option => option.group}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id && option.sourceType === value.sourceType}
        value={selectedSource}
        onChange={(_e, val) => handleSourceChange(val)}
        renderInput={params => (
          <TextField {...params} label={t("alertRules.selectDiagnostic")} />
        )}
        fullWidth
      />

      {/* Condition — hidden for warnings (presence) */}
      {valueKind !== "presence" && (
        <TextField
          select
          label={t("alertRules.condition")}
          value={conditionType}
          onChange={e => setConditionType(e.target.value as AlertConditionType)}
          size="small"
          fullWidth
        >
          {conditionOptions.map(c => (
            <MenuItem key={c.value} value={c.value}>
              {t(c.labelKey)}
            </MenuItem>
          ))}
        </TextField>
      )}

      {/* Threshold — numeric input for numeric sources */}
      {valueKind === "numeric" && needsSingleThreshold(conditionType) && (
        <TextField
          label={t("alertRules.threshold")}
          type="number"
          value={threshold}
          onChange={e => setThreshold(e.target.value)}
          size="small"
          fullWidth
        />
      )}

      {/* Threshold — dropdown for discrete sources */}
      {valueKind === "discrete" && conditionType === "equals" && selectedSource?.discreteValues && (
        <TextField
          select
          label={t("alertRules.threshold")}
          value={threshold}
          onChange={e => setThreshold(e.target.value)}
          size="small"
          fullWidth
        >
          {selectedSource.discreteValues.map(dv => (
            <MenuItem key={dv.value} value={dv.value.toString()}>
              {dv.label}
            </MenuItem>
          ))}
        </TextField>
      )}

      {/* Range thresholds */}
      {valueKind === "numeric" && needsRangeThreshold(conditionType) && (
        <Box display="flex" gap={2}>
          <TextField
            label={t("alertRules.thresholdMin")}
            type="number"
            value={thresholdMin}
            onChange={e => setThresholdMin(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label={t("alertRules.thresholdMax")}
            type="number"
            value={thresholdMax}
            onChange={e => setThresholdMax(e.target.value)}
            size="small"
            fullWidth
          />
        </Box>
      )}

      {/* Severity */}
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
          {t("alertRules.severity")}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={severity}
          onChange={(_e, val) => val && setSeverity(val)}
          fullWidth
        >
          <ToggleButton value="info">{t("alertRules.severityInfo")}</ToggleButton>
          <ToggleButton value="warning">{t("alertRules.severityWarning")}</ToggleButton>
          <ToggleButton value="critical">{t("alertRules.severityCritical")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Target */}
      <Box>
        <FormControlLabel
          control={<Switch checked={targetAll} onChange={(_e, checked) => setTargetAll(checked)} />}
          label={t("alertRules.allDevices")}
        />
        {!targetAll && (
          <TextField
            label={t("alertRules.deviceIds")}
            value={deviceIdsText}
            onChange={e => setDeviceIdsText(e.target.value)}
            size="small"
            fullWidth
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      {/* Sound */}
      <Box>
        <FormControlLabel
          control={<Switch checked={soundEnabled} onChange={(_e, checked) => setSoundEnabled(checked)} />}
          label={t("alertRules.sound")}
        />
        <FormControlLabel
          control={
            <Switch
              checked={soundRepeating}
              onChange={(_e, checked) => setSoundRepeating(checked)}
              disabled={!soundEnabled}
            />
          }
          label={t("alertRules.soundRepeating")}
          sx={{ color: soundEnabled ? "text.primary" : "text.disabled" }}
        />
      </Box>

      {/* Actions */}
      <Box display="flex" justifyContent="flex-end" gap={1} sx={{ pt: 1 }}>
        <Button onClick={onCancel}>{t("alertRules.cancel")}</Button>
        <Button variant="contained" onClick={handleSave} disabled={!isValid()}>
          {t("alertRules.save")}
        </Button>
      </Box>
    </Stack>
  );
};

export default AlertRuleForm;
